/**
 * A modern one-time-code input: `length` single-character boxes that behave like
 * the OS-level OTP fields users expect.
 *
 * - Typing a digit auto-advances focus to the next box.
 * - Backspace on an empty box clears and moves to the previous box.
 * - Pasting a full code into any box fills every box.
 * - Arrow keys move between boxes.
 * - Once every box holds a digit, {@link CodeInputProps.onComplete} fires so the
 *   caller can auto-submit without the user pressing Enter.
 *
 * Controlled: the parent owns the code string (digits only, up to `length`).
 * Non-digits are ignored.
 */
import { useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent, ChangeEvent } from 'react';

export interface CodeInputProps {
  /** The current code (digits only, 0..length characters). */
  readonly value: string;
  /** Called with the next code on every edit. */
  readonly onChange: (value: string) => void;
  /** Called once the code reaches full `length`. */
  readonly onComplete: (value: string) => void;
  /** Number of boxes. Defaults to 6. */
  readonly length?: number;
  /** Disable all boxes (e.g. while confirming). */
  readonly disabled?: boolean;
  /** Accessible name for the group of boxes. */
  readonly label: string;
  /** Focus the first box on mount. */
  readonly autoFocus?: boolean;
}

export function CodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  label,
  autoFocus = false,
}: CodeInputProps): React.JSX.Element {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const chars = Array.from({ length }, (_, index) => value[index] ?? '');

  function focusBox(index: number): void {
    const clamped = Math.max(0, Math.min(index, length - 1));
    inputs.current[clamped]?.focus();
    inputs.current[clamped]?.select();
  }

  function emit(nextChars: string[], focusIndex: number): void {
    const next = nextChars.join('');
    onChange(next);
    focusBox(focusIndex);
    if (next.length === length) {
      onComplete(next);
    }
  }

  function handleChange(index: number, event: ChangeEvent<HTMLInputElement>): void {
    const digits = event.target.value.replace(/\D/g, '');
    if (digits.length === 0) {
      return;
    }
    const nextChars = [...chars];
    if (digits.length > 1) {
      // A multi-character insert (autofill or fast paste-into-box): distribute.
      let cursor = index;
      for (const digit of digits) {
        if (cursor >= length) {
          break;
        }
        nextChars[cursor] = digit;
        cursor += 1;
      }
      emit(nextChars, cursor);
      return;
    }
    nextChars[index] = digits;
    emit(nextChars, index + 1);
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const nextChars = [...chars];
      if (nextChars[index]) {
        nextChars[index] = '';
        emit(nextChars, index);
      } else if (index > 0) {
        nextChars[index - 1] = '';
        emit(nextChars, index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>): void {
    event.preventDefault();
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (digits.length === 0) {
      return;
    }
    const nextChars = Array.from({ length }, (_, index) => digits[index] ?? '');
    emit(nextChars, digits.length);
  }

  return (
    <div className="code-input" role="group" aria-label={label}>
      {chars.map((char, index) => (
        <input
          // Boxes are positional and never reorder, so the index key is stable.
          key={index}
          ref={(node) => {
            inputs.current[index] = node;
          }}
          className="code-input__box"
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1}`}
          maxLength={1}
          value={char}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}
